import React, {useEffect, useMemo, useState} from 'react';
import {useHistory} from "react-router-dom";
import ToggleComponent from "v4/components/utils/ToggleComponent/ToggleComponent";
import {TASK_ADD, TASK_FORM, TASK_ITEM, TASK_ITEM_FORM} from "v4/data/apiRoutes";
import {useNavigation} from "v4/features/front/navigation/contexts/NavigationContext";
import FormEntityLayout from "v4/layouts/FormEntityLayout/FormEntityLayout";
import ShowEntityLayout from "v4/layouts/ShowEntityLayout/ShowEntityLayout";
import ProspectOldMailTasksTabStats
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/ProspectOldMailTasksTab/components/ProspectOldMailTasksTabStats/ProspectOldMailTasksTabStats";
import MoveTaskButton from "v4/features/front/moveTask/components/MoveTaskButton/MoveTaskButton";
import ProspectTabBackButton from "v4/components/utils/ProspectTabBackButton/ProspectTabBackButton";
import ProspectTabEditButton from "v4/components/utils/ProspectTabEditButton/ProspectTabEditButton";
import ProspectTabShowActions
    from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/partials/ProspectTabShowActions/ProspectTabShowActions";
import {usePolTranslation} from "v4/hooks/usePolTranslation";

export default function BaseTaskTab({
                                        listingComponent,
                                        resumeComponent = null,
                                        onGetData: onGetDataFromProps = null,
                                        fetchFormParameters: fetchFormParametersFromProps,
                                        showResume = false
                                    }) {
    const {t} = usePolTranslation();
    const {currentRoute} = useNavigation();
    const isShow = currentRoute.routeType === 'SHOW';
    const isEdit = currentRoute.routeType === 'EDIT';
    const isAdd = currentRoute.routeType === 'ADD';
    const isListing = currentRoute.routeType === 'LISTING';
    const isResume = showResume && (currentRoute.params.resume === 'resume' || currentRoute.routeType === 'RESUME');

    const {prospect: prospectId, task: showId} = currentRoute.params;
    const [taskData, setTaskData] = useState(null);
    const [relaunchParameters, setRelaunchParameters] = useState({});

    const {location: {state, search}, replace} = useHistory();
    const queryParameters = useMemo(() => new URLSearchParams(search), [search]);
    const quoteId = queryParameters.get('quoteId') ?? null;

    useEffect(() => {
        if (state?.relaunchId) {
            const {relaunchId, ...newState} = state;
            replace({state: newState})
            setRelaunchParameters({quoteId: relaunchId});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.relaunchId]);

    const finalFetchFormParameters = useMemo(() => ({
        id: showId,
        prospectId,
        quoteId,
        ...relaunchParameters,
        ...(fetchFormParametersFromProps ?? {})
    }), [showId, prospectId, quoteId, relaunchParameters, fetchFormParametersFromProps]);

    const onGetData = (data) => {
        setTaskData(data?.['hydra:member'] ?? data);
        onGetDataFromProps && onGetDataFromProps(data);
    }

    return (
        <>
            <ToggleComponent isShowing={isListing}>
                {listingComponent}
            </ToggleComponent>

            <ToggleComponent isShowing={isShow && !isEdit}
                             shouldRemoveFromDom={true}
                             className="prospect-tab__prospect showing">
                <div className="prospect-tab__prospect__header">
                    {taskData?.metadata?.isDraft && (
                        <span className="prospect-tab__prospect__header__draft">{t('task_isDraft')}</span>
                    )}
                    <ProspectTabBackButton/>
                    <ProspectTabEditButton/>
                    <MoveTaskButton taskId={showId} className="prospect-tab__prospect__header__action"/>
                    {taskData?.metadata?.showActions && (
                        <ProspectTabShowActions showId={showId}
                                                prospectId={prospectId}
                                                showActions={taskData.metadata.showActions}/>
                    )}
                </div>
                <ShowEntityLayout entityId={showId}
                                  onGetData={onGetData}
                                  additionalClassName={taskData?.metadata?.isDraft ? 'is-draft' : ''}
                                  entityRouteName={TASK_ITEM_FORM}
                                  fetchFormParameters={fetchFormParametersFromProps}>
                    <ProspectOldMailTasksTabStats entityId={showId}/>
                </ShowEntityLayout>
            </ToggleComponent>

            <ToggleComponent isShowing={isAdd || isEdit}
                             shouldRemoveFromDom={true}
                             className="prospect-tab__prospect editing">
                <FormEntityLayout formRouteName={isAdd ? TASK_FORM : TASK_ITEM_FORM}
                                  submitRouteName={isAdd ? TASK_ADD : TASK_ITEM}
                                  fetchFormParameters={finalFetchFormParameters}
                                  whenSubmitted={whenSubmitted}/>
            </ToggleComponent>

            {resumeComponent && (
                <ToggleComponent isShowing={isResume}
                                 shouldRemoveFromDom={true}
                                 className="resume-tab">
                    {resumeComponent}
                </ToggleComponent>
            )}
        </>
    )
}

function whenSubmitted(history) {
    history.push(history.location.pathname.replace(/\/(edit|add)$/, ''), {hasToRefresh: true});
}
